import * as React from 'react'
import {
  Label,
  LabelProps,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import '../exposure-chart.css'
import { GUID } from '../guids'
import '../recharts.css'
import { Result } from '../result'
import {
  ClientExposuresTransactions,
  LimitType,
  AlertLevels,
  AlertLevelsDict,
  MutedMetrics,
} from '../types/types'
import {
  getHighestLevel,
  obfuscateSymbol,
} from '../utils'
import { ClientAlert, ClientCode, ClientConfig } from "../types/clients";

export interface Props {
  readonly clearSelection: () => void
  readonly clients: ReadonlyArray<ClientExposuresTransactions>
  readonly guidLookup: Record<ClientCode, GUID | undefined>
  readonly metricLevels: AlertLevelsDict
  readonly selectedClientCode: ClientCode | undefined
  readonly toggleMute: (clientCode: ClientCode, metric: LimitType) => () => void
  readonly tvMode: boolean
  readonly onClientSelectorClick: (selectBySymbol: string) => void
  readonly activeClientInfo: Result<ActiveClientInfo, NoActiveClientReasons>
}

export interface ActiveClientInfo {
  readonly state: ClientConfig
  readonly metricLevels: AlertLevels
  readonly mutedMetrics: MutedMetrics
  readonly values: ClientExposuresTransactions
}

export type NoActiveClientReasons =
  | 'no client selected'
  | 'no settings found for given client'
  | 'no data for given client'

const chartMargin = { top: 30, right: 20, bottom: 30, left: 80 }

const yAxisLabel: string | number | LabelProps = {
  angle: -90,
  offset: -65,
  position: 'insideLeft',
  value: 'Realistic Exposure Value',
  style: { textAnchor: 'middle' }
}

function RevExposureChart(props: Props): JSX.Element {
  const {
    clients,
    metricLevels,
    selectedClientCode,
    tvMode,
    onClientSelectorClick,
  } = props;

  const clientsTypeWorkaround = clients as ClientExposuresTransactions[];

  const transformedData: TransformedDataItem[] = clientsTypeWorkaround.reduce<TransformedDataItem[]>((acc, item) => {
    acc.push(
      { ...item, xValue: 0, yValue: item.revExposureOrdersPercent },  // Position for value1
      { ...item, xValue: 1, yValue: item.historicMaxRevExposurePercent }   // Position for value2
    );
    return acc;
  }, []);

  const dataForLeft = transformedData.filter(item => item.xValue === 0);
  const dataForRight = transformedData.filter(item => item.xValue === 1);

  // Determine the overall min and max for the unified Y-axis domain
  const allData = [...dataForLeft, ...dataForRight];
  const maxValue = Math.max(...allData.map(item => item.yValue));


  return (
    <div className='main-chart-grid' style={{ width: '20%' }} >
      <ResponsiveContainer width='80%' height={820}>
        <ScatterChart margin={chartMargin}>
          <XAxis
            domain={[0, 1]}
            type='number'
            dataKey="xValue"
            hide={true}
          />
          <YAxis
            name="historicMaxRevExposurePercent"
            label={yAxisLabel}
            unit='%'
            tickFormatter={tickFormatter}
            type='number'
            yAxisId="left" dataKey="yValue" orientation="left" domain={[0, Math.max(100, maxValue)]}
          />
          <YAxis name="historicMaxRevExposurePercent" unit='%' hide={true} yAxisId="right" dataKey="yValue" orientation="right" domain={[0, Math.max(100, maxValue)]} />
          <ReferenceLine
            y={90}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='90%'
            yAxisId="left"
          />
          <ReferenceLine
            y={100}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='100%'
            yAxisId="left"
          />
          <ReferenceLine
            y={200}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='200%'
            yAxisId="left"
          />
          <ReferenceLine
            y={500}
            stroke='#88888888'
            strokeDasharray='3 3'
            label='500%'
            yAxisId="left"
          />
          <Scatter
            name='Client'
            yAxisId="left"
            data={dataForLeft}
            // TODO: using this custom shape greatly increases the number of repaints; can we avoid that?
            shape={scatterPoint(
              clients.map(c => c.code),
              tvMode,
              metricLevels,
              selectedClientCode,
              false
            )}
            onMouseDown={onClientSelectorClick}
          />
          <Scatter
            name='MaxRevClient'
            yAxisId="right"
            data={dataForRight}
            shape={scatterPoint(
              clients.map(c => c.code),
              tvMode,
              metricLevels,
              selectedClientCode,
              true
            )}
            onMouseDown={onClientSelectorClick}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}

function tickFormatter(value: number): string {
  return value.toFixed(0)
}

interface TransformedDataItem extends ClientExposuresTransactions {
  xValue: number;
  yValue: number;
}

interface ScatterPointProps {
  readonly cx: number
  readonly cy: number
  readonly fill: string
  readonly height: number
  readonly key: string
  readonly node: {
    readonly x: number
    readonly y: number
    readonly z: '-'
  }
  readonly payload: ClientExposuresTransactions
  readonly size: number
  readonly tooltipPosition: {
    readonly x: number
    readonly y: number
  }
  readonly width: number
  readonly x: number
  readonly y: number
  readonly xValue: number
  readonly yValue: number
  // do we need to implement types for these?
  // tooltipPayload:
  // xAxis:
  // yAxis:
  // zAxis:
}

type CustomShapeFunction = (props: unknown) => JSX.Element;

function scatterPoint(
  clientCodes: ReadonlyArray<ClientCode>,
  tvMode: boolean,
  metricLevels: AlertLevelsDict,
  selectedClient: ClientCode | undefined,
  isMaxRev: boolean
): CustomShapeFunction {
  return function _scatterPoint(props: unknown): JSX.Element {
    if (!isScatterPointProps(props)) {
      throw new Error("Invalid props passed to scatterPoint: " + props);
    }

    const { fill, width, height, x, y, cx, cy, payload } = props; // Destructuring is safe after type check
    const isSelected = selectedClient === payload.code
    const cxPointPosition = isMaxRev ? 150 : 50;
    const cxClientPosition = isMaxRev ? 140 : 40;
    const maxRevFill = isMaxRev ? 'dashboard-ghost-rev-point__fill alert-level-badge--' : 'dashboard-point alert-level-badge--';

    const groupClasses = [
      'recharts-layerrecharts-scatter-symbol',
      maxRevFill +
      getHighestLevel(
        metricLevels,
        {},
        payload.code,
      ).toLowerCase(),
      isSelected ? ' dashboard-point--selected' : '',
    ].join(' ')

    if (isMaxRev) {
      return (
        <g
          className={groupClasses}
          transform={'translate(' + cx + ' ' + cy + ')'}
        >
          <circle r={5} cx={cxPointPosition} cy={0} className='dashboard-point__fill' />
          {/* Width & height are semi-arbitrary, but should be large enough to
        contain the symbol */}
          <foreignObject
            className='node'
            width={110}
            height={70}
            x={cxClientPosition} 
            y={-55}
            style={{ pointerEvents: 'none' }}
          >
            <div className='scatter-point__text__wrapper' >
              <div className='scatter-point__text_ghost'>
              {tvMode ?
                <svg width="60px" height="60px" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="alert-level-badge__text_ghost">
                    <path opacity="0.7" d="M22 19.723V12.3006C22 6.61173 17.5228 2 12 2C6.47715 2 2 6.61173 2 12.3006V19.723C2 21.0453 3.35098 21.9054 4.4992 21.314C5.42726 20.836 6.5328 20.9069 7.39614 21.4998C8.36736 22.1667 9.63264 22.1667 10.6039 21.4998L10.9565 21.2576C11.5884 20.8237 12.4116 20.8237 13.0435 21.2576L13.3961 21.4998C14.3674 22.1667 15.6326 22.1667 16.6039 21.4998C17.4672 20.9069 18.5727 20.836 19.5008 21.314C20.649 21.9054 22 21.0453 22 19.723Z" stroke="gray" strokeWidth="0.7" />
                    <text x="12" y="18" fontSize="14" textAnchor="middle">{obfuscateSymbol(clientCodes.indexOf(payload.code))}</text>
                </svg>
                :
                <svg width="60px" height="60px" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="alert-level-badge__text_ghost">
                  <path fill="black" d="M16 10.5C16 11.3284 15.5523 12 15 12C14.4477 12 14 11.3284 14 10.5C14 9.67157 14.4477 9 15 9C15.5523 9 16 9.67157 16 10.5Z" className="alert-level-badge__text_ghost" />
                  <ellipse fill="black" cx="9" cy="10.5" rx="1" ry="1.5" className="alert-level-badge__text_ghost" />
                  <path opacity="0.7" d="M22 19.723V12.3006C22 6.61173 17.5228 2 12 2C6.47715 2 2 6.61173 2 12.3006V19.723C2 21.0453 3.35098 21.9054 4.4992 21.314C5.42726 20.836 6.5328 20.9069 7.39614 21.4998C8.36736 22.1667 9.63264 22.1667 10.6039 21.4998L10.9565 21.2576C11.5884 20.8237 12.4116 20.8237 13.0435 21.2576L13.3961 21.4998C14.3674 22.1667 15.6326 22.1667 16.6039 21.4998C17.4672 20.9069 18.5727 20.836 19.5008 21.314C20.649 21.9054 22 21.0453 22 19.723Z" stroke="gray" strokeWidth="0.7" />
                  <text x="12" y="18" fontSize="6" textAnchor="middle" fill="black">{payload.code}</text>
                </svg>
              }
            </div>
            </div>
          </foreignObject>
        </g>
      )
    }

    else {
      return (
        <g
          className={groupClasses}
          transform={'translate(' + cx + ' ' + cy + ')'}
        >
          <circle r={5} cx={cxPointPosition} cy={0} className='dashboard-point__fill' />
          {/* Width & height are semi-arbitrary, but should be large enough to
        contain the symbol */}
          <foreignObject
            className='node'
            width={100}
            height={40}
            x={cxClientPosition}
            y={-40}
            style={{ pointerEvents: 'none' }}
          >
            <div className='scatter-point__text__wrapper' >
              <div className='scatter-point__text alert-level-badge__text'>
                {tvMode ? <span className='tv-mode-symbol'>{obfuscateSymbol(clientCodes.indexOf(payload.code))}</span> : payload.code}
              </div>
            </div>
          </foreignObject>
        </g>
      )
    }
  }
}

// ensuring data structures passed are type-safe and clearly defined
function isScatterPointProps(props: any): props is ScatterPointProps {
  props.cx = 90;
  return typeof props === 'object' &&
    props !== null &&
    'cx' in props && typeof props.cx === 'number' &&
    'cy' in props && typeof props.cy === 'number' &&
    'payload' in props && typeof props.payload === 'object' &&
    'code' in props.payload; // Ensure payload structure matches
}

export default RevExposureChart
